import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useFhirRealtimeOptional } from '../../../hooks/fhirRealtimeContext';
import {
  fetchPendingServiceRequestsForPatient,
  type PendingServiceRequestCard,
} from '../utils/pendingServiceRequest';

export interface UsePendingServiceRequestsResult {
  requests: PendingServiceRequestCard[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * True when a finished load owns the loading flag and must clear it.
 *
 * Deliberately ignores whether this load turned the flag on. A realtime refresh
 * (`showLoading: false`) can supersede the initial foreground load, and it still has to clear
 * the flag that load set — otherwise the skeleton never goes away.
 */
export function shouldClearPendingRequestsLoading(opts: {
  loadSeq: number;
  latestSeq: number;
  showLoading: boolean;
}): boolean {
  return opts.loadSeq === opts.latestSeq;
}

export function usePendingServiceRequests(
  patientId?: string,
): UsePendingServiceRequestsResult {
  const medplum = useMedplum();
  const [requests, setRequests] = useState<PendingServiceRequestCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const hasRequestsRef = useRef(false);
  const loadSeqRef = useRef(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    hasRequestsRef.current = false;
    loadSeqRef.current += 1;
    setRequests([]);
    setError(null);
    setLoading(!!patientId);
  }, [patientId]);

  const loadRequests = useCallback(
    async (opts?: { showLoading?: boolean }) => {
      if (!patientId) {
        setRequests([]);
        setLoading(false);
        return;
      }

      const seq = ++loadSeqRef.current;
      const showLoading = opts?.showLoading ?? !hasRequestsRef.current;
      if (showLoading) setLoading(true);
      setError(null);

      try {
        try {
          medplum.invalidateSearches('ServiceRequest');
        } catch {
          // Cache invalidation must not block the fetch.
        }

        const cards = await fetchPendingServiceRequestsForPatient(medplum, patientId);
        if (seq !== loadSeqRef.current) return;

        setRequests(cards);
        setError(null);
        hasRequestsRef.current = true;
      } catch {
        if (seq !== loadSeqRef.current) return;
        setError('load_failed');
        setRequests([]);
      } finally {
        if (
          shouldClearPendingRequestsLoading({
            loadSeq: seq,
            latestSeq: loadSeqRef.current,
            showLoading,
          })
        ) {
          setLoading(false);
        }
      }
    },
    [medplum, patientId],
  );

  const loadRequestsRef = useRef(loadRequests);
  loadRequestsRef.current = loadRequests;

  useEffect(() => {
    void loadRequests({ showLoading: true });
  }, [loadRequests, tick]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const fhirRealtime = useFhirRealtimeOptional();
  useEffect(() => {
    if (!fhirRealtime) return;
    return fhirRealtime.onClientPortalRefresh(() => {
      void loadRequestsRef.current({ showLoading: false });
    });
  }, [fhirRealtime]);

  return { requests, loading, error, refresh };
}
